import useChatStore, { CAPABILITIES_CHANGE } from '@/Components/Chat/store';
import useActionsCapabilitiesStore from '@/Features/Core/Components/ActionsCapabilities/store';
import { MODEL_TYPES } from '@/Features/Core/Components/ActionsCapabilities/utils';
import useSmartModeStore from '@/Features/SmartMode/store';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  PhotoIcon,
  PlayCircleIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import { inject, observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ActionButton from './components/ActionButton';
import ModelSelector from './components/ModelSelector';

const ActionsCapabilities = ({ store }) => {
  const {
    selectedModel,
    setSelectedModel,
    selectedModelParameters,
    setSelectedModelParameters,
    generationsModelsActive,
    setGenerationsModelsActive,
  } = useActionsCapabilitiesStore();

  const [imagesModels, setImagesModels] = useState([]);
  const [videosModels, setVideosModels] = useState([]);
  const [activeReasoning, setActiveReasoning] = useState(false);
  const [textToSpeechModels, setTextToSpeechModels] = useState([]);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const scrollContainerRef = useRef(null);

  // Previous state backup
  const [previousState, setPreviousState] = useState({
    combo: null,
    models: [],
    capabilities: [],
  });

  const getModelsByType = async (type) => {
    try {
      const response = await store.api.get(`/v2/ai/models?type=${type}`);
      const data = await response.data;
      return data.data;
    } catch (error) {
      return [];
    }
  };

  const getImagesModels = async () => {
    const models = await getModelsByType('image');
    setImagesModels(models);
  };

  const getVideosModels = async () => {
    const models = await getModelsByType('video');
    setVideosModels(models);
  };

  const getTextToSpeechModels = async () => {
    const models = await getModelsByType('audio');
    setTextToSpeechModels(models);
  };

  useEffect(() => {
    (async () => {
      await getImagesModels();
      await getVideosModels();
      await getTextToSpeechModels();
    })();
  }, []);

  const [capabilities, setCapabilities] = useState([]);
  const {
    combos,
    selectedCombo,
    selectedModels,
    setSelectedCombo,
    selectedCapabilities,
    allModels,
    updateSelectedModels,
    setSelectedCapabilities,
    addEvent,
  } = useChatStore();

  const { setSmartMode } = useSmartModeStore();

  // Save current state before switching to special modes
  const saveCurrentState = () => {
    // Don't save anything if current combo is Reasoning or Web Browsing
    if (
      selectedCombo?.name === 'Reasoning' ||
      selectedCombo?.name === 'Web Browsing'
    ) {
      setPreviousState({
        combo: null,
        models: [],
        capabilities: [],
      });
      return;
    }

    setPreviousState({
      combo: selectedCombo,
      models: [...selectedModels],
      capabilities: [...selectedCapabilities],
    });
  };

  // Restore previous state when deactivating special modes
  const restorePreviousState = () => {
    if (previousState.combo) {
      setSelectedCombo(previousState.combo);
    }
    if (previousState.models.length > 0) {
      updateSelectedModels(() => previousState.models);
    }
    // Use handleCapabilities to properly trigger the CAPABILITIES_CHANGE event
    handleCapabilities(previousState.capabilities);
  };

  const getCapabilities = async () => {
    try {
      const response = await store.api.get(`/capability`);
      setCapabilities(response.data.capabilities);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getCapabilities();
  }, []);

  useEffect(() => {
    if (generationsModelsActive || activeReasoning) {
      setSmartMode('off');
    }
  }, [generationsModelsActive, activeReasoning]);

  useEffect(() => {
    if (selectedModel) {
      // If reasoning was active, restore previous state before deactivating
      if (activeReasoning) {
        setActiveReasoning(false);
        restorePreviousState();
      } else {
        // Otherwise just remove browser capabilities
        handleCapabilities(
          selectedCapabilities.filter(
            (capability) => capability.type !== 'browser'
          )
        );
      }
    }
  }, [selectedModel]);

  const handleCapabilities = (capabilities) => {
    setSelectedCapabilities(capabilities);
    addEvent(CAPABILITIES_CHANGE);
  };

  const handleWebBrowsingCapability = () => {
    setActiveReasoning(false);
    // Toggle the capability of web browsing
    if (
      selectedCapabilities.find((capability) => capability.type === 'browser')
    ) {
      // Deactivating: restore previous state
      restorePreviousState();
    } else {
      // Activating: save current state first, then switch to Web Browsing
      saveCurrentState();
      selectComboByName('Web Browsing');
      const webBrowsingCapability = capabilities.find(
        (capability) => capability.type === 'browser'
      );
      handleCapabilities([webBrowsingCapability]);
    }
  };

  const handleReasoningCapability = () => {
    // Always remove browser capability when toggling reasoning
    handleCapabilities(
      selectedCapabilities.filter((capability) => capability.type !== 'browser')
    );

    if (activeReasoning) {
      // Deactivating: restore previous state
      setActiveReasoning(false);
      restorePreviousState();
    } else {
      // Activating: save current state first, then switch to Reasoning
      saveCurrentState();
      selectComboByName('Reasoning');
      setActiveReasoning(true);
    }
  };

  const selectComboByName = (comboName) => {
    const combo = combos.find((c) => c.name === comboName);
    if (!combo) {
      console.error(`Combo "${comboName}" not found`);
      return;
    }

    setSelectedCombo(combo);

    if (combo && Array.isArray(combo.llms)) {
      try {
        const selectedModels = combo.llms
          .map((llmId) => allModels.find((m) => m._id === llmId))
          .filter(Boolean)
          .sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

        updateSelectedModels(() => selectedModels);
        localStorage.setItem('currentModels', JSON.stringify(selectedModels));
      } catch (error) {
        console.error('Error processing combo:', error);
      }
    }
  };

  const isActive = (type) =>
    selectedCapabilities.find((cap) => cap.type === type);

  const shouldShowButton = (type) => {
    // Si es un tipo de modelo (image, video, audio)
    if (MODEL_TYPES[type]) {
      const isModelActive =
        selectedModel?.type?.toLowerCase() === MODEL_TYPES[type];
      const isAnyModelActive = Object.values(MODEL_TYPES).some(
        (modelType) => selectedModel?.type?.toLowerCase() === modelType
      );

      // Mostrar si es el modelo activo o si ningún modelo está activo
      return isModelActive || !isAnyModelActive;
    }

    // Para otros tipos de botones (browser), ocultarlos si hay un modelo activo
    return !selectedModel;
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      const hasOverflow = scrollWidth > clientWidth;

      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(
        hasOverflow && scrollLeft < scrollWidth - clientWidth - 1
      );
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -200,
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScrollButtons();
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);

      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [imagesModels, videosModels, textToSpeechModels, selectedModel]);

  return (
    <div className="relative flex items-center w-full">
      {/* Left Arrow */}
      {showLeftArrow && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 z-10 flex items-center justify-center size-6 bg-white dark:bg-night-black border border-gray-200 dark:border-tornado-cloud rounded-full shadow-md hover:bg-gray-50 dark:hover:bg-tornado-cloud transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="size-4 text-gray-600 dark:text-gray-300" />
        </button>
      )}

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex items-center gap-2 w-full p-1 overflow-x-scroll no-scrollbar"
        onScroll={checkScrollButtons}
      >
        {shouldShowButton(MODEL_TYPES.image) && (
          <ModelSelector
            name="Images"
            type={MODEL_TYPES.image}
            icon={PhotoIcon}
            models={imagesModels}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            setGenerationsModelsActive={setGenerationsModelsActive}
            selectedModelParameters={selectedModelParameters}
            setSelectedModelParameters={setSelectedModelParameters}
          />
        )}

        {shouldShowButton(MODEL_TYPES.video) && (
          <ModelSelector
            customFunction={() => {
              toast(
                'Remember to attach the image you want to convert into a video',
                {
                  icon: '🎥',
                  duration: 5000,
                  position: 'top-right',
                }
              );
            }}
            name="Videos"
            type={MODEL_TYPES.video}
            icon={PlayCircleIcon}
            models={videosModels}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            setGenerationsModelsActive={setGenerationsModelsActive}
            selectedModelParameters={selectedModelParameters}
            setSelectedModelParameters={setSelectedModelParameters}
          />
        )}

        {shouldShowButton(MODEL_TYPES.audio) && (
          <ModelSelector
            name="Text to speech"
            type={MODEL_TYPES.audio}
            icon={SpeakerWaveIcon}
            models={textToSpeechModels}
            selectedModel={selectedModel}
            setSelectedModel={setSelectedModel}
            setGenerationsModelsActive={setGenerationsModelsActive}
            selectedModelParameters={selectedModelParameters}
            setSelectedModelParameters={setSelectedModelParameters}
          />
        )}

        {shouldShowButton(MODEL_TYPES.reasoning) && (
          <ActionButton
            tooltip="Helps the model analyze complex inputs and break down problems to provide better answers."
            icon={Cog6ToothIcon}
            text="Reasoning"
            onClick={handleReasoningCapability}
            active={activeReasoning}
          />
        )}

        {shouldShowButton(MODEL_TYPES.browser) && (
          <ActionButton
            tooltip="Grants web browsing capability to LLMs that support real-time data access."
            icon={GlobeAltIcon}
            text="Web browsing"
            onClick={handleWebBrowsingCapability}
            active={isActive(MODEL_TYPES.browser)}
          />
        )}
      </div>

      {/* Right Arrow */}
      {showRightArrow && (
        <button
          onClick={scrollRight}
          className="absolute right-0 z-10 flex items-center justify-center size-6 bg-white dark:bg-night-black border border-gray-200 dark:border-tornado-cloud rounded-full shadow-md hover:bg-gray-50 dark:hover:bg-tornado-cloud transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="size-4 text-gray-600 dark:text-gray-300" />
        </button>
      )}
    </div>
  );
};

export default inject('store')(observer(ActionsCapabilities));
